<template>
    <div class="col-6 col-sm-4 col-xl-3 mb-2">
        <div class="rental-card" :class="`card-${statusConfig.key}`" @click="to ? $router.push(to) : $emit('click')">

            <!-- Conteúdo principal -->
            <div class="rental-body">

            <!-- Badge de status -->
            <span class="rental-status" :class="`status-${statusConfig.key}`">
                <span class="dot"></span>
                {{ statusConfig.label }}
            </span>

            <!-- Data da próxima ação (entrega/retirada/coleta/devolução), embaixo do status -->
            <p v-if="nextActionDate" class="rental-next-action">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                Previsto: {{ dateFormat.date(nextActionDate) }}
            </p>

            <!-- Cliente -->
            <p class="rental-client">{{ clientName }}</p>

            <!-- ID -->
            <p class="rental-id">Code: {{ rentalId }}</p>

            <!--
                showValue: true  → exibe o valor monetário
                showValue: false → exibe a cidade/local
            -->
            <div v-show="showValue">
              <p class="rental-value">{{ formatCurrency(value) }}</p>
            </div>
            <p class="rental-value rental-value--location">
                <!-- retirada na loja → ícone de loja -->
                <template v-if="deliveryType === 'pickup'">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px;flex-shrink:0">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                Retirada na loja
                </template>
                <!-- entrega → ícone de localização + cidade -->
                <template v-else>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px;flex-shrink:0">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                </svg>
                {{ location ?? '—' }}
                </template>
            </p>

            <!-- Meta: data início + data fim em linhas separadas -->
            <div class="rental-meta">
                <div class="rental-meta-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <div class="rental-dates">
                    <span class="rental-date-row">
                    <span class="date-tag">Início</span>
                    {{ dateFormat.dateTime(startDate) }}
                    </span>
                    <span class="rental-date-row">
                    <span class="date-tag">Fim</span>
                    {{ dateFormat.dateTime(endDate) }}
                    </span>
                </div>
                </div>
            </div>

            <!-- Divisor -->
            <hr class="rental-divider"/>

            <!-- Rodapé: botão + tipo de entrega -->
            <div class="rental-footer">
                <slot name="action">
                <button class="btn-detalhes" @click.stop="to ? $router.push(to) : $emit('click')">
                    Detalhes →
                </button>
                </slot>

                <div class="rental-delivery">
                <!-- ícone entrega -->
                <template v-if="deliveryType === 'delivery'">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="1" y="3" width="15" height="13" rx="1"/>
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                    <circle cx="5.5" cy="18.5" r="2.5"/>
                    <circle cx="18.5" cy="18.5" r="2.5"/>
                    </svg>
                    Entrega
                </template>
                <!-- ícone retirada -->
                <template v-else>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                    </svg>
                    Retirada
                </template>
                </div>
            </div>

            </div>
        </div>
    </div>
</template>

<!-- STATUS_MAP fora do <script setup> para poder ser usado no validator -->
<script>
export const STATUS_MAP = {
  PENDING_DELIVERY:       { key: 'pending-delivery',       label: 'Aguardando entrega'   },
  PENDING_PICKUP:         { key: 'pending-pickup',         label: 'Aguardando retirada'  },
  ACTIVE:                 { key: 'active',                 label: 'No espaço do cliente' },
  PENDING_COMPANY_PICKUP: { key: 'pending-company-pickup', label: 'Aguardando coleta'    },
  PENDING_RETURN:         { key: 'pending-return',         label: 'Aguardando devolução' },
  COMPLETED:              { key: 'completed',              label: 'Locação encerrada'    },
  SCHEDULED:              { key: 'scheduled',              label: 'Agendada'             },
}
</script>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import dateFormat from '@/services/dateFormat.js'

// ══════════════════════════════════════════════
// PROPS
// ══════════════════════════════════════════════
const props = defineProps({
  /** String do banco: 'PENDING_DELIVERY', 'ACTIVE', etc. */
  status: {
    type: String,
    required: true,
    validator: v => Object.keys(STATUS_MAP).includes(v),
  },

  rentalId:     { type: [Number, String], required: true },
  clientName:   { type: String,           required: true },
  value:        { type: Number,           default: 0    },

  /**
   * true  → exibe o valor monetário em destaque
   * false → exibe a cidade/local em destaque
   */
  showValue:    { type: Boolean, default: true },

  startDate:    { type: String, required: true },   // ISO: '2025-03-10'
  endDate:      { type: String, required: true },   // ISO: '2025-03-15'
  /** Data da próxima ação da locação (delivery_date ou pickup_date, conforme o status) — null quando não há (ex: encerrada) */
  nextActionDate: { type: String, default: null },
  location:     { type: String, default: null  },

  /** 'delivery' | 'pickup' */
  deliveryType: { type: String, default: 'pickup' },

  /** router-link ao clicar no card */
  to:           { type: String, default: null },
})

defineEmits(['click'])

// ══════════════════════════════════════════════
// COMPUTED
// ══════════════════════════════════════════════
const statusConfig = computed(() =>
  STATUS_MAP[props.status] ?? { key: 'unknown', label: props.status }
)

// ══════════════════════════════════════════════
// FORMATADORES
// ══════════════════════════════════════════════
function formatCurrency(v) {
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}


</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');

/* ══════════════════════════════
   CARD BASE
══════════════════════════════ */
.rental-card {
  background: #fff;
  border-radius: 16px;
  border: 0.5px solid rgba(0,0,0,0.08);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  cursor: pointer;
  height: 100%;
  font-family: 'Sora', sans-serif;
}
.rental-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.07);
}

/* Barra colorida no topo */
.rental-card::before {
  content: '';
  display: block;
  height: 3px;
  width: 100%;
}

/* ── Cores por status ── */
.card-pending-delivery::before       { background: linear-gradient(90deg, #378ADD, #85B7EB); }
.card-pending-pickup::before         { background: linear-gradient(90deg, #BA7517, #EF9F27); }
.card-active::before                 { background: linear-gradient(90deg, #1D9E75, #5DCAA5); }
.card-pending-company-pickup::before { background: linear-gradient(90deg, #D85A30, #F0997B); }
.card-pending-return::before         { background: linear-gradient(90deg, #EF9F27, #FAC775); }
.card-completed::before              { background: linear-gradient(90deg, #3B6D11, #97C459); }
.card-scheduled::before              { background: linear-gradient(90deg, #534AB7, #AFA9EC); }
.card-unknown::before                { background: linear-gradient(90deg, #888780, #B4B2A9); }

/* ══════════════════════════════
   CORPO
══════════════════════════════ */
.rental-body { padding: 16px 16px 14px; }

/* ── Badge de status ── */
.rental-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 6px;
  white-space: nowrap;
}
.rental-status .dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }

.rental-next-action {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #6b6b6b;
  margin: 0 0 12px;
}

.status-pending-delivery       { background: #E6F1FB; color: #185FA5; }
.status-pending-delivery .dot  { background: #378ADD; }

.status-pending-pickup         { background: #FAEEDA; color: #854F0B; }
.status-pending-pickup .dot    { background: #BA7517; }

.status-active                 { background: #E1F5EE; color: #0F6E56; }
.status-active .dot            { background: #1D9E75; }

.status-pending-company-pickup       { background: #FAECE7; color: #993C1D; }
.status-pending-company-pickup .dot  { background: #D85A30; }

.status-pending-return         { background: #FAEEDA; color: #854F0B; }
.status-pending-return .dot    { background: #EF9F27; }

.status-completed              { background: #EAF3DE; color: #3B6D11; }
.status-completed .dot         { background: #97C459; }

.status-scheduled              { background: #EEEDFE; color: #534AB7; }
.status-scheduled .dot         { background: #AFA9EC; }

.status-unknown                { background: #F1EFE8; color: #5F5E5A; }
.status-unknown .dot           { background: #888780; }

/* ── Textos ── */
.rental-client {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rental-id    { font-size: 11px; color: #b0b0b0; margin: 0 0 10px; }

/* variação: quando showValue=false, exibe cidade no lugar do valor */
.rental-value--location {
  display: flex;
  align-items: center;
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #6b6b6b;
}

.rental-dates      { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.rental-date-row   { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #6b6b6b; white-space: nowrap; }
.date-tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #b0b0b0;
  min-width: 30px;
}
.rental-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0 0 12px;
}

/* ── Meta ── */
.rental-meta       { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; }
.rental-meta-item  { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #9a9a9a; }
.rental-meta-item svg { flex-shrink: 0; }

/* ── Divisor ── */
.rental-divider { border: none; border-top: 0.5px solid rgba(0,0,0,0.07); margin: 0 0 12px; }

/* ── Rodapé ── */
.rental-footer   { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.rental-delivery { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #b0b0b0; }

/* ── Botão ── */
.btn-detalhes {
  font-family: 'Sora', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;
  border: 0.5px solid rgba(0,0,0,0.12);
  background: transparent;
  color: #6b6b6b;
  transition: all 0.15s;
  cursor: pointer;
  white-space: nowrap;
}
.btn-detalhes:hover { background: #1a1a1a; color: #fff; border-color: #1a1a1a; }
</style>